import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  IconButton,
  Paper
} from '@mui/material';
import {
  DirectionsCar as TravelIcon,
  Home as DwellIcon,
  Schedule as TimeIcon,
  LocationOn as LocationIcon,
  PictureAsPdf as PdfIcon,
  Memory as AiIcon,
  MyLocation as ObservationIcon,
  Slideshow as PowerPointIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import pathfinderApi from '../../services/pathfinderApi';
import { 
  exportToPowerPoint as generatePowerPoint, 
  captureMapScreenshot, 
  ChartData,
  TopLocation,
  RepeatedVisit,
  CoLocation,
  PowerPointTheme,
  THEME_DEFINITIONS 
} from '../../utils/powerpointExport';
import { batchReverseGeocode } from '../../utils/geocoding';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface InfrastructureFinding {
  point_id: string;
  lat: number;
  lon: number;
  infrastructure: Array<{
    id: number;
    name: string;
    type: string;
    icon: string;
    distance_meters: number;
  }>;
}

interface StoryModeProps {
  dataPoints: any[];
  dwellLocations: any[];
  currentTime: Date | null;
  selectedStudents: string[];
  infrastructureFindings?: InfrastructureFinding[];
  onMapCenterChange?: (center: [number, number]) => void;
  onTimeChange?: (time: Date) => void;
}

interface TimelineEvent {
  id: string;
  type: 'travel' | 'dwell' | 'observation';  // Added 'observation' for single points
  startTime: Date;
  endTime: Date;
  entityId: string;
  deviceType: string;
  // Travel-specific
  distance?: number;
  pointCount?: number;
  startLocation?: [number, number];
  endLocation?: [number, number];
  // Dwell-specific
  location?: [number, number];
  duration?: number;
  address?: string;
  // Observation-specific (for sparse data)
  isInferred?: boolean;
  metadata?: {
    country?: string;
    carrier?: string;
    timezone?: string;
    ipAddress?: string;
  };
}

const StoryMode: React.FC<StoryModeProps> = ({
  dataPoints,
  dwellLocations,
  currentTime,
  selectedStudents,
  infrastructureFindings = [],
  onMapCenterChange,
  onTimeChange
}) => {
  const activeEventRef = useRef<HTMLLIElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  
  // PowerPoint export state
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<PowerPointTheme>('standard');
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState('');
  const [isPdfExporting, setIsPdfExporting] = useState(false);

  // Helper functions (defined before useMemo to avoid reference errors)
  const toRad = (degrees: number): number => {
    return (degrees * Math.PI) / 180;
  };

  // Haversine formula for distance between two coordinates
  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Filter dataPoints to only show selected students/devices
  const filteredDataPoints = useMemo(() => {
    if (selectedStudents.length === 0) return dataPoints;
    return dataPoints.filter(p => 
      selectedStudents.includes(p.studentId) || 
      selectedStudents.includes(p.deviceId) ||
      selectedStudents.includes(`GN-${p.deviceId}`)
    );
  }, [dataPoints, selectedStudents]);
  
  // Filter dwells to only show selected students/devices
  // Handle GN- prefix and case-insensitive matching
  const filteredDwells = useMemo(() => {
    if (selectedStudents.length === 0) return dwellLocations;
    return dwellLocations.filter((d: any) => {
      const dwellId = (d.entity_id || '').toLowerCase();
      return selectedStudents.some(s => {
        const studentId = s.toLowerCase();
        // Exact match, or handle GN- prefix on either side
        return studentId === dwellId ||
               studentId === `gn-${dwellId}` ||
               `gn-${studentId}` === dwellId ||
               studentId.replace(/^gn-/, '') === dwellId.replace(/^gn-/, '');
      });
    });
  }, [dwellLocations, selectedStudents]);

  // Generate timeline events from tracks and dwells (using filtered data)
  // IMPROVED: Smart detection for sparse GeoNeutron data
  const timelineEvents = useMemo(() => {
    const events: TimelineEvent[] = [];
    let eventCounter = 0;

    // Add dwell events from pre-detected dwells (from PoL analysis)
    filteredDwells.forEach(dwell => {
      events.push({
        id: `dwell-${dwell.id}`,
        type: 'dwell',
        startTime: new Date(dwell.start_time),
        endTime: new Date(dwell.end_time),
        entityId: dwell.entity_id,
        deviceType: dwell.device_type,
        location: [dwell.latitude, dwell.longitude],
        duration: dwell.duration_seconds,
        address: `${dwell.latitude.toFixed(5)}, ${dwell.longitude.toFixed(5)}`
      });
    });

    // Sort points by entity and time
    const sortedPoints = [...filteredDataPoints].sort((a, b) => {
      const entityCompare = (a.studentId || a.deviceId).localeCompare(b.studentId || b.deviceId);
      if (entityCompare !== 0) return entityCompare;
      return a.timestamp.getTime() - b.timestamp.getTime();
    });

    // SMART SPARSE DATA HANDLING:
    // For GeoNeutron data with infrequent reporting, we need different logic:
    // 1. Single points that aren't during a dwell = "Observation" (not "Traveling")
    // 2. Multiple points close together in space but far apart in time = potential dwell location
    // 3. Only mark as "Traveling" if we have multiple points with significant distance

    const LOCATION_CLUSTER_THRESHOLD = 500; // 500 meters - points closer = same location
    const MIN_TRAVEL_DISTANCE = 100; // 100 meters - minimum to consider as travel
    const TIME_GAP_THRESHOLD = 60 * 60 * 1000; // 1 hour gap = new segment

    // Group points by entity
    const pointsByEntity: { [key: string]: any[] } = {};
    sortedPoints.forEach(point => {
      const entityId = point.studentId || point.deviceId;
      if (!pointsByEntity[entityId]) pointsByEntity[entityId] = [];
      pointsByEntity[entityId].push(point);
    });

    // Process each entity's points
    Object.entries(pointsByEntity).forEach(([entityId, points]) => {
      if (points.length === 0) return;

      // For each point, determine if it's an observation, part of a dwell, or travel
      let currentSegment: any = null;

      points.forEach((point, index) => {
        const deviceType = point.deviceType || 'external';
        const prevPoint = index > 0 ? points[index - 1] : null;
        
        // Check if this point is already covered by a detected dwell
        // Handle GN- prefix and case-insensitive matching
        const isDuringDwell = filteredDwells.some(dwell => {
          const dwellEntityId = (dwell.entity_id || '').toLowerCase().replace(/^gn-/, '');
          const pointEntityId = entityId.toLowerCase().replace(/^gn-/, '');
          if (dwellEntityId !== pointEntityId) return false;
          
          const pointTime = point.timestamp.getTime();
          const dwellStart = new Date(dwell.start_time).getTime();
          const dwellEnd = new Date(dwell.end_time).getTime();
          return pointTime >= dwellStart && pointTime <= dwellEnd;
        });

        if (isDuringDwell) {
          // Finalize any current segment
          if (currentSegment) {
            events.push(finalizeSegment(currentSegment, eventCounter++));
            currentSegment = null;
          }
          return; // Skip - already in a dwell event
        }

        // Calculate distance from previous point
        // Coordinates are GeoJSON: [lng, lat], haversineDistance expects (lat, lon)
        let distanceFromPrev = 0;
        let timeSincePrev = 0;
        if (prevPoint) {
          distanceFromPrev = haversineDistance(
            prevPoint.coordinates[1], prevPoint.coordinates[0],  // lat, lon
            point.coordinates[1], point.coordinates[0]           // lat, lon
          ) * 1000; // Convert km to meters
          timeSincePrev = point.timestamp.getTime() - prevPoint.timestamp.getTime();
        }

        // Determine segment type based on movement and time
        const isNewSegment = !currentSegment || 
                            currentSegment.entityId !== entityId ||
                            timeSincePrev > TIME_GAP_THRESHOLD ||
                            (distanceFromPrev > LOCATION_CLUSTER_THRESHOLD && timeSincePrev > 5 * 60 * 1000);

        if (isNewSegment) {
          // Finalize previous segment
          if (currentSegment) {
            events.push(finalizeSegment(currentSegment, eventCounter++));
          }

          // Start new segment
          currentSegment = {
            entityId,
            deviceType,
            startTime: point.timestamp,
            lastTime: point.timestamp,
            points: [point],
            totalDistance: 0
          };
        } else {
          // Continue current segment
          currentSegment.lastTime = point.timestamp;
          currentSegment.points.push(point);
          currentSegment.totalDistance += distanceFromPrev;
        }
      });

      // Finalize last segment
      if (currentSegment) {
        events.push(finalizeSegment(currentSegment, eventCounter++));
      }
    });

    // Helper function to finalize a segment and determine its type
    function finalizeSegment(segment: any, id: number): TimelineEvent {
      const { entityId, deviceType, startTime, lastTime, points, totalDistance } = segment;
      const durationMs = lastTime.getTime() - startTime.getTime();
      const durationSec = durationMs / 1000;
      const firstPoint = points[0];
      const lastPoint = points[points.length - 1];

      // Determine type based on data characteristics
      let type: 'travel' | 'dwell' | 'observation';
      
      if (points.length === 1) {
        // Single point = observation
        type = 'observation';
      } else if (totalDistance < MIN_TRAVEL_DISTANCE && durationSec > 60) {
        // Multiple points, little movement, some time = dwell
        type = 'dwell';
      } else if (totalDistance > MIN_TRAVEL_DISTANCE) {
        // Significant movement = travel
        type = 'travel';
      } else {
        // Default to observation for sparse data
        type = 'observation';
      }

      // Calculate centroid for location (coordinates are GeoJSON: [lng, lat])
      const avgLat = points.reduce((sum: number, p: any) => sum + p.coordinates[0], 0) / points.length;
      const avgLon = points.reduce((sum: number, p: any) => sum + p.coordinates[1], 0) / points.length;

      // Extract metadata if available
      const metadata = {
        country: firstPoint.country || firstPoint.metadata?.country,
        carrier: firstPoint.carrier || firstPoint.metadata?.carrier,
        timezone: firstPoint.timezone || firstPoint.metadata?.timezone,
        ipAddress: firstPoint.ip_address || firstPoint.ipAddress
      };

      return {
        id: `${type}-${entityId}-${id}`,
        type,
        startTime,
        endTime: lastTime,
        entityId,
        deviceType,
        location: [avgLat, avgLon],
        distance: totalDistance / 1000, // Convert to km
        pointCount: points.length,
        startLocation: firstPoint.coordinates,
        endLocation: lastPoint.coordinates,
        duration: durationSec,
        address: `${avgLat.toFixed(5)}, ${avgLon.toFixed(5)}`,
        metadata
      };
    }

    // Sort all events by start time
    const sortedEvents = events.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    
    
    return sortedEvents;
  }, [filteredDataPoints, filteredDwells, haversineDistance]);

  // Find active event based on current time
  const activeEventIndex = useMemo(() => {
    if (!currentTime) return -1;
    const currentTimeMs = currentTime.getTime();
    const index = timelineEvents.findIndex(event => {
      const startMs = event.startTime.getTime();
      const endMs = event.endTime.getTime();
      return currentTimeMs >= startMs && currentTimeMs <= endMs;
    });
    
    // Debug logging (occasionally)
    if (Math.random() < 0.1) { // 10% of the time
    }
    
    return index;
  }, [timelineEvents, currentTime]);

  // Auto-scroll to active event
  useEffect(() => {
    if (activeEventRef.current) {
      activeEventRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [activeEventIndex]);

  // Format duration
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format distance
  const formatDistance = (km: number): string => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km.toFixed(2)}km`;
  };

  // Calculate chart data for PowerPoint export (defined before generateAISummary so it can be used there)
  const calculateChartData = useCallback((): ChartData => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Daily rhythm (activity by hour)
    const hourCounts: Record<number, number> = {};
    for (let h = 0; h < 24; h++) hourCounts[h] = 0;
    filteredDataPoints.forEach(point => {
      const hour = point.timestamp.getHours();
      hourCounts[hour]++;
    });
    
    const dailyRhythm = Object.entries(hourCounts).map(([hour, count]) => ({
      hour: `${hour.padStart(2, '0')}:00`,
      activity: count
    }));
    
    // Weekly activity
    const dayCounts: Record<string, number> = {};
    days.forEach(d => dayCounts[d] = 0);
    filteredDataPoints.forEach(point => {
      const day = days[point.timestamp.getDay()];
      dayCounts[day]++;
    });
    
    const weeklyActivity = days.map(day => ({
      day,
      activity: dayCounts[day]
    }));
    
    // Top locations from dwells - include lat/lng for geocoding
    const locationMap = new Map<string, { 
      visits: number; 
      duration: number; 
      lat: number; 
      lng: number;
      entities: Set<string>;
      firstVisit: string;
      lastVisit: string;
    }>();
    
    filteredDwells.forEach((dwell: any) => {
      // Use coordinates as the unique key (rounded to 4 decimal places for grouping nearby locations)
      const coordKey = `${dwell.latitude.toFixed(4)},${dwell.longitude.toFixed(4)}`;
      const existing = locationMap.get(coordKey);
      const entityId = dwell.entity_id || dwell.studentId || 'unknown';
      
      if (existing) {
        existing.visits++;
        existing.duration += dwell.duration_seconds / 60; // Convert to minutes
        existing.entities.add(entityId);
        // Update first/last visit times
        if (dwell.start_time < existing.firstVisit) existing.firstVisit = dwell.start_time;
        if (dwell.end_time > existing.lastVisit) existing.lastVisit = dwell.end_time;
      } else {
        locationMap.set(coordKey, { 
          visits: 1, 
          duration: dwell.duration_seconds / 60,
          lat: dwell.latitude,
          lng: dwell.longitude,
          entities: new Set([entityId]),
          firstVisit: dwell.start_time,
          lastVisit: dwell.end_time
        });
      }
    });
    
    // Build top locations with coordinates for geocoding
    const topLocations: TopLocation[] = Array.from(locationMap.entries())
      .map(([coordKey, data]) => ({
        location: coordKey, // Will be replaced by place name during export
        visits: data.visits,
        duration: Math.round(data.duration),
        lat: data.lat,
        lng: data.lng,
        entities: Array.from(data.entities)
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 12);
    
    // Calculate repeated visits (locations visited 2+ times)
    const repeatedVisits: RepeatedVisit[] = Array.from(locationMap.entries())
      .filter(([_, data]) => data.visits >= 2)
      .map(([coordKey, data]) => ({
        location: coordKey, // Will be replaced by place name during export
        lat: data.lat,
        lng: data.lng,
        visitCount: data.visits,
        totalDuration: Math.round(data.duration),
        firstVisit: data.firstVisit,
        lastVisit: data.lastVisit,
        entities: Array.from(data.entities)
      }))
      .sort((a, b) => b.visitCount - a.visitCount)
      .slice(0, 15);
    
    // Calculate co-locations (locations visited by multiple entities)
    const coLocations: CoLocation[] = Array.from(locationMap.entries())
      .filter(([_, data]) => data.entities.size >= 2)
      .map(([coordKey, data]) => ({
        location: coordKey, // Will be replaced by place name during export
        lat: data.lat,
        lng: data.lng,
        entities: Array.from(data.entities),
        overlapCount: data.visits,
        totalOverlapTime: Math.round(data.duration)
      }))
      .sort((a, b) => b.entities.length - a.entities.length || b.overlapCount - a.overlapCount)
      .slice(0, 15);
    
    // Stats
    const uniqueDates = new Set(filteredDataPoints.map(p => p.timestamp.toDateString()));
    const peakHour = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
    
    return {
      dailyRhythm,
      weeklyActivity,
      topLocations,
      stats: {
        totalPoints: filteredDataPoints.length,
        uniqueDevices: new Set(filteredDataPoints.map(p => p.deviceId || p.studentId)).size,
        totalDwells: filteredDwells.length,
        peakHour: peakHour ? parseInt(peakHour[0]) : null,
        avgPointsPerDay: uniqueDates.size > 0 ? Math.round(filteredDataPoints.length / uniqueDates.size) : 0
      },
      repeatedVisits: repeatedVisits.length > 0 ? repeatedVisits : undefined,
      coLocations: coLocations.length > 0 ? coLocations : undefined
    };
  }, [filteredDataPoints, filteredDwells]);

  // Generate AI summary of timeline
  const generateAISummary = useCallback(async () => {
    if (timelineEvents.length === 0) return;
    
    setIsLoadingAI(true);
    try {
      // Prepare timeline data for AI analysis
      const travelEvents = timelineEvents.filter(e => e.type === 'travel');
      const dwellEvents = timelineEvents.filter(e => e.type === 'dwell');
      const observationEvents = timelineEvents.filter(e => e.type === 'observation');
      
      // Calculate travel distance (sum of actual distances moved)
      const totalTravelDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
      
      // For travel time, only count events with actual duration (endTime > startTime)
      // and reasonable durations (less than 24 hours per segment)
      const totalTravelTime = travelEvents.reduce((sum, e) => {
        const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
        // Only count if duration is positive and less than 24 hours
        return sum + (duration > 0 && duration < 86400 ? duration : 0);
      }, 0);
      
      // For dwell time, use the actual duration field if available (from PoL analysis)
      // Otherwise calculate from timestamps, but cap at reasonable values
      const totalDwellTime = dwellEvents.reduce((sum, e) => {
        // Prefer the duration field (set by PoL analysis)
        if (e.duration && e.duration > 0) {
          return sum + e.duration;
        }
        // Fallback: calculate from timestamps but cap at 4 hours per dwell
        const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
        return sum + (duration > 0 && duration < 14400 ? duration : 0);
      }, 0);
      
      // Get unique locations from dwells (filter out undefined/null values)
      const uniqueDwellLocations = new Set(
        dwellEvents
          .map(e => e.address)
          .filter((addr): addr is string => Boolean(addr))
      );
      
      // Also get unique observation locations
      const uniqueObservationLocations = new Set(
        observationEvents
          .map(e => e.address)
          .filter((addr): addr is string => Boolean(addr))
      );
      
      // Summarize infrastructure findings for AI
      const infrastructureSummary = infrastructureFindings.length > 0 
        ? infrastructureFindings.flatMap(f => 
            f.infrastructure.map(i => ({
              type: i.type,
              name: i.name,
              distance_meters: i.distance_meters
            }))
          )
        : [];
      
      // Get chart data which includes repeated visits and co-locations
      const chartData = calculateChartData();
      
      const summary = await pathfinderApi.generateStoryModeAI({
        entities: selectedStudents,
        totalEvents: timelineEvents.length,
        travelCount: travelEvents.length,
        dwellCount: dwellEvents.length,
        observationCount: observationEvents.length,
        totalTravelDistance,
        totalTravelTime,
        totalDwellTime,
        uniqueLocations: Array.from(uniqueDwellLocations),
        uniqueObservationLocations: Array.from(uniqueObservationLocations),
        infrastructureProximity: infrastructureSummary, // Include infrastructure findings
        // Send simplified event data to reduce token usage
        timelineEvents: timelineEvents.map(e => ({
          type: e.type,
          startTime: e.startTime.toISOString(),
          endTime: e.endTime.toISOString(),
          entityId: e.entityId,
          deviceType: e.deviceType,
          // Use actual duration field for dwells, otherwise calculate
          duration: e.duration || Math.max(0, (e.endTime.getTime() - e.startTime.getTime()) / 1000),
          distance: e.distance,
          address: e.address,
          pointCount: e.pointCount
        })),
        // Include repeated visits and co-locations for enhanced analysis
        repeatedVisits: chartData.repeatedVisits?.map(rv => ({
          location: rv.location,
          visitCount: rv.visitCount,
          totalDuration: rv.totalDuration,
          entities: rv.entities
        })),
        coLocations: chartData.coLocations?.map(cl => ({
          location: cl.location,
          entities: cl.entities,
          overlapCount: cl.overlapCount,
          totalOverlapTime: cl.totalOverlapTime
        }))
      });
      
      setAiSummary(summary.summary);
    } catch (error) {
      setAiSummary('AI analysis failed. Please try again.');
    } finally {
      setIsLoadingAI(false);
    }
  }, [timelineEvents, selectedStudents, infrastructureFindings, calculateChartData]);

  // Open theme selection dialog
  const handlePowerPointClick = () => {
    setThemeDialogOpen(true);
  };

  // Export to PowerPoint function (called after theme selection)
  const exportToPowerPoint = async (theme: PowerPointTheme) => {
    setThemeDialogOpen(false);
    setIsExporting(true);
    setExportStatus('Capturing map screenshot...');
    
    try {
      // Prepare dwell points for numbering overlays in exported slides
      const dwellPointsForProjection = timelineEvents
        .filter(e => e.type === 'dwell' && e.location)
        .map(e => ({
          id: e.id,
          lat: e.location![0],
          lon: e.location![1]
        }));

      // Capture map screenshot with dimensions + projected dwell pixel coordinates
      const mapScreenshot = await captureMapScreenshot('pathfinder-map-container', dwellPointsForProjection);

      // Calculate chart data from available data
      setExportStatus('Processing chart data...');
      const chartData: ChartData = calculateChartData();

      // Enrich locations with place names from geocoding API
      setExportStatus('Resolving place names...');
      try {
        // Collect all unique coordinates that need geocoding
        const coordsToGeocode: Array<{ lat: number; lng: number; id: string }> = [];
        const coordIdMap = new Map<string, string>(); // coordKey -> id
        
        // From top locations
        chartData.topLocations.forEach((loc, idx) => {
          if (loc.lat && loc.lng) {
            const id = `top-${idx}`;
            coordsToGeocode.push({ lat: loc.lat, lng: loc.lng, id });
            coordIdMap.set(`${loc.lat.toFixed(4)},${loc.lng.toFixed(4)}`, id);
          }
        });
        
        // From repeated visits
        if (chartData.repeatedVisits) {
          chartData.repeatedVisits.forEach((rv, idx) => {
            const coordKey = `${rv.lat.toFixed(4)},${rv.lng.toFixed(4)}`;
            if (!coordIdMap.has(coordKey)) {
              const id = `repeat-${idx}`;
              coordsToGeocode.push({ lat: rv.lat, lng: rv.lng, id });
              coordIdMap.set(coordKey, id);
            }
          });
        }
        
        // From co-locations
        if (chartData.coLocations) {
          chartData.coLocations.forEach((coloc, idx) => {
            const coordKey = `${coloc.lat.toFixed(4)},${coloc.lng.toFixed(4)}`;
            if (!coordIdMap.has(coordKey)) {
              const id = `coloc-${idx}`;
              coordsToGeocode.push({ lat: coloc.lat, lng: coloc.lng, id });
              coordIdMap.set(coordKey, id);
            }
          });
        }
        
        if (coordsToGeocode.length > 0) {
          const placeNames = await batchReverseGeocode(coordsToGeocode);
          
          // Create a lookup by coordinate key
          const placeNameByCoord = new Map<string, string>();
          coordsToGeocode.forEach(coord => {
            const coordKey = `${coord.lat.toFixed(4)},${coord.lng.toFixed(4)}`;
            const placeName = placeNames.get(coord.id);
            if (placeName) {
              placeNameByCoord.set(coordKey, placeName);
            }
          });
          
          // Apply place names to top locations
          chartData.topLocations.forEach(loc => {
            if (loc.lat && loc.lng) {
              const coordKey = `${loc.lat.toFixed(4)},${loc.lng.toFixed(4)}`;
              loc.placeName = placeNameByCoord.get(coordKey);
            }
          });
          
          // Apply place names to repeated visits
          if (chartData.repeatedVisits) {
            chartData.repeatedVisits.forEach(rv => {
              const coordKey = `${rv.lat.toFixed(4)},${rv.lng.toFixed(4)}`;
              const placeName = placeNameByCoord.get(coordKey);
              if (placeName) {
                rv.location = placeName;
              }
            });
          }
          
          // Apply place names to co-locations
          if (chartData.coLocations) {
            chartData.coLocations.forEach(coloc => {
              const coordKey = `${coloc.lat.toFixed(4)},${coloc.lng.toFixed(4)}`;
              const placeName = placeNameByCoord.get(coordKey);
              if (placeName) {
                coloc.location = placeName;
              }
            });
          }
        }
      } catch (geocodeError) {
        console.warn('Failed to resolve place names:', geocodeError);
        // Continue with coordinates as fallback
      }

      // Auto-generate AI narrative for the export
      let aiNarrative: string | undefined;
      if (timelineEvents.length > 0) {
        try {
          setExportStatus('Generating AI insights... (this may take a moment)');
          const travelEvents = timelineEvents.filter(e => e.type === 'travel');
          const dwellEvents = timelineEvents.filter(e => e.type === 'dwell');
          const observationEvents = timelineEvents.filter(e => e.type === 'observation');
          
          const totalTravelDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
          const totalTravelTime = travelEvents.reduce((sum, e) => {
            const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
            return sum + (duration > 0 && duration < 86400 ? duration : 0);
          }, 0);
          const totalDwellTime = dwellEvents.reduce((sum, e) => {
            if (e.duration && e.duration > 0) return sum + e.duration;
            const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
            return sum + (duration > 0 && duration < 14400 ? duration : 0);
          }, 0);
          
          const uniqueDwellLocations = new Set(dwellEvents.map(e => e.address).filter(Boolean));
          const uniqueObservationLocations = new Set(observationEvents.map(e => e.address).filter(Boolean));
          
          const aiResponse = await pathfinderApi.generateStoryModeAI({
            entities: selectedStudents,
            totalEvents: timelineEvents.length,
            travelCount: travelEvents.length,
            dwellCount: dwellEvents.length,
            observationCount: observationEvents.length,
            totalTravelDistance,
            totalTravelTime,
            totalDwellTime,
            uniqueLocations: Array.from(uniqueDwellLocations) as string[],
            uniqueObservationLocations: Array.from(uniqueObservationLocations) as string[],
            infrastructureProximity: [],
            timelineEvents: timelineEvents.map(e => ({
              type: e.type,
              startTime: e.startTime.toISOString(),
              endTime: e.endTime.toISOString(),
              entityId: e.entityId,
              deviceType: e.deviceType,
              duration: e.duration || Math.max(0, (e.endTime.getTime() - e.startTime.getTime()) / 1000),
              distance: e.distance,
              address: e.address,
              pointCount: e.pointCount
            })),
            // Include repeated visits and co-locations (already enriched with place names)
            repeatedVisits: chartData.repeatedVisits?.map(rv => ({
              location: rv.location,
              visitCount: rv.visitCount,
              totalDuration: rv.totalDuration,
              entities: rv.entities
            })),
            coLocations: chartData.coLocations?.map(cl => ({
              location: cl.location,
              entities: cl.entities,
              overlapCount: cl.overlapCount,
              totalOverlapTime: cl.totalOverlapTime
            }))
          });
          aiNarrative = aiResponse.summary;
        } catch (err) {
          console.warn('AI narrative generation failed:', err);
        }
      }

      // Export to PowerPoint
      setExportStatus(`Building PowerPoint presentation (${THEME_DEFINITIONS[theme].name})...`);
      await generatePowerPoint({
        title: 'OSINT Explorer Story Timeline Report',
        subtitle: `Generated ${new Date().toLocaleDateString()}`,
        events: timelineEvents.map(e => ({
          ...e,
          tripCount: undefined,
          visitCount: undefined
        })),
        entities: selectedStudents,
        mapScreenshot,
        chartData,
        aiNarrative,
        includeMap: true,
        includeSummary: true,
        includeTimeline: true,
        includeCharts: true,
        theme
      });

      setExportStatus('Download complete!');
      setTimeout(() => {
        setIsExporting(false);
        setExportStatus('');
      }, 1500);
    } catch (error) {
      console.error('Failed to export to PowerPoint:', error);
      setIsExporting(false);
      setExportStatus('');
      alert('Failed to export to PowerPoint. Please try again.');
    }
  };

  // Click handler for timeline events
  const handleEventClick = useCallback((event: TimelineEvent) => {
    
    // Get the location for this event
    let location: [number, number] | undefined;
    
    if ((event.type === 'dwell' || event.type === 'observation') && event.location) {
      location = event.location;
    } else if (event.type === 'travel' && event.startLocation) {
      location = event.startLocation; // Start of travel
    }
    
    // Update map center if location exists
    if (location && onMapCenterChange) {
      onMapCenterChange(location);
    }
    
    // Update time slider to event start time
    if (onTimeChange) {
      onTimeChange(event.startTime);
    }
  }, [onMapCenterChange, onTimeChange]);

  if (timelineEvents.length === 0) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption">No timeline data available. Load track and dwell data to see the story timeline.</Typography>
        </Alert>
      </Box>
    );
  }

  /** Rough strip of common markdown so PDF plain text doesn't show ###, **, ---, etc. */
  function stripMarkdownForPlainPdf(text: string): string {
    if (!text) return '';
    return (
      text
        // ATX headings
        .replace(/^#{1,6}\s+/gm, '')
        // hr lines
        .replace(/^\s*[-*_]{3,}\s*$/gm, '')
        // bold-italic ***
        .replace(/\*\*\*([\s\S]*?)\*\*\*/g, '$1')
        // bold ** or __
        .replace(/\*\*([\s\S]*?)\*\*/g, '$1')
        .replace(/__([\s\S]*?)__/g, '$1')
        // italic * or _ (single pair; good enough for reports)
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
        .replace(/(^|[^_])_([^_\n]+)_([^_]|$)/g, '$1$2$3')
    );
  }

  // Export to PDF function
  const exportToPDF = async () => {
    setIsPdfExporting(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Story Mode Timeline Report', pageWidth / 2, 20, { align: 'center' });

      // Subtitle
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`Entities: ${selectedStudents.join(', ')}`, pageWidth / 2, 34, { align: 'center' });
      doc.text(`Total Events: ${timelineEvents.length}`, pageWidth / 2, 40, { align: 'center' });

      // 1) Use existing summary if available
      // 2) If not available, generate one now
      let summaryForPdf = aiSummary?.trim() || '';
      if (!summaryForPdf || summaryForPdf === 'AI analysis failed. Please try again.') {
        try {
          const travelEvents = timelineEvents.filter(e => e.type === 'travel');
          const dwellEvents = timelineEvents.filter(e => e.type === 'dwell');
          const observationEvents = timelineEvents.filter(e => e.type === 'observation');

          const totalTravelDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
          const totalTravelTime = travelEvents.reduce((sum, e) => {
            const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
            return sum + (duration > 0 && duration < 86400 ? duration : 0);
          }, 0);
          const totalDwellTime = dwellEvents.reduce((sum, e) => {
            if (e.duration && e.duration > 0) return sum + e.duration;
            const duration = (e.endTime.getTime() - e.startTime.getTime()) / 1000;
            return sum + (duration > 0 && duration < 14400 ? duration : 0);
          }, 0);

          const uniqueDwellLocations = new Set(
            dwellEvents.map(e => e.address).filter((addr): addr is string => Boolean(addr))
          );
          const uniqueObservationLocations = new Set(
            observationEvents.map(e => e.address).filter((addr): addr is string => Boolean(addr))
          );

          const infrastructureSummary =
            infrastructureFindings.length > 0
              ? infrastructureFindings.flatMap(f =>
                  f.infrastructure.map(i => ({
                    type: i.type,
                    name: i.name,
                    distance_meters: i.distance_meters
                  }))
                )
              : [];

          const chartData = calculateChartData();

          const summaryResponse = await pathfinderApi.generateStoryModeAI({
            entities: selectedStudents,
            totalEvents: timelineEvents.length,
            travelCount: travelEvents.length,
            dwellCount: dwellEvents.length,
            observationCount: observationEvents.length,
            totalTravelDistance,
            totalTravelTime,
            totalDwellTime,
            uniqueLocations: Array.from(uniqueDwellLocations),
            uniqueObservationLocations: Array.from(uniqueObservationLocations),
            infrastructureProximity: infrastructureSummary,
            timelineEvents: timelineEvents.map(e => ({
              type: e.type,
              startTime: e.startTime.toISOString(),
              endTime: e.endTime.toISOString(),
              entityId: e.entityId,
              deviceType: e.deviceType,
              duration: e.duration || Math.max(0, (e.endTime.getTime() - e.startTime.getTime()) / 1000),
              distance: e.distance,
              address: e.address,
              pointCount: e.pointCount
            })),
            repeatedVisits: chartData.repeatedVisits?.map(rv => ({
              location: rv.location,
              visitCount: rv.visitCount,
              totalDuration: rv.totalDuration,
              entities: rv.entities
            })),
            coLocations: chartData.coLocations?.map(cl => ({
              location: cl.location,
              entities: cl.entities,
              overlapCount: cl.overlapCount,
              totalOverlapTime: cl.totalOverlapTime
            }))
          });

          summaryForPdf = summaryResponse?.summary?.trim() || '';
          if (summaryForPdf) setAiSummary(summaryForPdf); // cache for UI tab reuse
        } catch (err) {
          summaryForPdf = '';
          console.warn('AI summary generation for PDF failed:', err);
        }
      }

      // AI section before table
      let y = 48;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('AI Timeline Analysis', margin, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      const aiBody = summaryForPdf || 'AI timeline analysis was not available for this export.';
      const plainTextAIBody = stripMarkdownForPlainPdf(aiBody);
      const aiLines = doc.splitTextToSize(plainTextAIBody, contentWidth);
      const lineHeight = 5;
      const reserveTableHeader = 18;

      for (const line of aiLines) {
        if (y + lineHeight + reserveTableHeader > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += lineHeight;
      }

      y += 4;
      let tableStartY = y;
      if (tableStartY + reserveTableHeader > pageHeight - margin) {
        doc.addPage();
        tableStartY = margin;
      }

      // Prepare table data
      const tableData = timelineEvents.map((event, index) => {
        const duration = (event.endTime.getTime() - event.startTime.getTime()) / 1000;
        const durationStr = duration >= 3600
          ? `${(duration / 3600).toFixed(1)}h`
          : duration >= 60
            ? `${(duration / 60).toFixed(0)}m`
            : `${duration.toFixed(0)}s`;

        const startStr = `${event.startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${event.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
        const endStr = `${event.endTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${event.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;

        let details = '';
        if (event.type === 'travel') {
          details = event.distance
            ? `${event.distance.toFixed(2)} km, ${event.pointCount} GPS points`
            : `${event.pointCount} GPS points`;
        } else if (event.type === 'observation') {
          const metaParts = [];
          if (event.metadata?.country) metaParts.push(event.metadata.country);
          if (event.metadata?.carrier) metaParts.push(event.metadata.carrier);
          details = event.address || `${event.location?.[0].toFixed(5)}, ${event.location?.[1].toFixed(5)}`;
          if (metaParts.length > 0) details += ` (${metaParts.join(', ')})`;
        } else {
          details = event.address || `${event.location?.[0].toFixed(5)}, ${event.location?.[1].toFixed(5)}`;
        }

        const typeDisplay = event.type === 'travel' ? 'Traveling'
          : event.type === 'observation' ? 'Observed'
          : 'Dwelling';

        return [
          index + 1,
          typeDisplay,
          startStr,
          endStr,
          durationStr,
          `${event.entityId} (${event.deviceType})`,
          details
        ];
      });

      // Add table
      autoTable(doc, {
        startY: tableStartY, // dynamic now
        head: [['#', 'Type', 'Start', 'End', 'Duration', 'Entity', 'Details']],
        body: tableData,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35 },
          4: { cellWidth: 20 },
          5: { cellWidth: 35 },
          6: { cellWidth: 'auto' }
        },
        didDrawPage: (data) => {
          const pageCount = (doc as any).internal.pages.length - 1;
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
          );
        }
      });

      // Save PDF
      const filename = `story-timeline-${selectedStudents.join('-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
    } finally{
      setIsPdfExporting(false);
    }
  };

  // Check if this is meaningful PoL data (has dwells or data for selected devices)
  const hasDwells = filteredDwells.length > 0;
  const uniqueDevicesInData = new Set(filteredDataPoints.map(p => p.studentId || p.deviceId)).size;
  const hasData = filteredDataPoints.length > 0 || hasDwells;
  
  // Calculate event type counts for summary
  const observationCount = timelineEvents.filter(e => e.type === 'observation').length;
  const dwellCount = timelineEvents.filter(e => e.type === 'dwell').length;
  const travelCount = timelineEvents.filter(e => e.type === 'travel').length;
  
  // Show message if no specific devices selected or no data
  if (!hasData || (uniqueDevicesInData > 10 && !hasDwells)) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Story Timeline
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          Story Mode is designed for Pattern of Life (PoL) analysis of individual devices.
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          To view a timeline:
          <br />
          1. Use <strong>Data Discovery</strong> to find devices
          <br />
          2. Click on a device in the results
          <br />
          3. Load their <strong>Pattern of Life</strong> data
          <br />
          4. View the timeline here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 0 }}>
            Story Timeline
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {timelineEvents.length} events:
            </Typography>
            {observationCount > 0 && (
              <Chip 
                label={`${observationCount} observed`} 
                size="small" 
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {dwellCount > 0 && (
              <Chip 
                label={`${dwellCount} dwells`} 
                size="small" 
                color="success"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {travelCount > 0 && (
              <Chip 
                label={`${travelCount} travel`} 
                size="small" 
                color="info"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={exportToPDF}
            disabled={isPdfExporting || isExporting }
            title="Export to PDF"
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {isPdfExporting ? <CircularProgress size={16} color="inherit" /> : <PdfIcon fontSize="small" />}
          </IconButton>
          <IconButton
            size="small"
            onClick={handlePowerPointClick}
            disabled={isPdfExporting || isExporting}
            title="Export to PowerPoint"
            sx={{ 
              bgcolor: 'secondary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'secondary.dark' }
            }}
          >
            {isExporting ? <CircularProgress size={16} color="inherit" /> : <PowerPointIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
      >
        <Tab label="Timeline" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
        <Tab label="AI Insights" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
      </Tabs>

      {/* Timeline Tab */}
      {activeTab === 0 && (
        <List sx={{ flex: 1, overflow: 'auto', mt: 1 }}>
        {timelineEvents.map((event, index) => {
          const isActive = index === activeEventIndex;
          const duration = (event.endTime.getTime() - event.startTime.getTime()) / 1000;

          return (
            <React.Fragment key={event.id}>
              <ListItem
                ref={isActive ? activeEventRef : null}
                onClick={() => handleEventClick(event)}
                sx={{
                  bgcolor: isActive ? 'grey.800' : 'transparent',
                  borderRadius: 1,
                  mb: 1,
                  transition: 'background-color 0.3s, transform 0.2s',
                  opacity: currentTime && currentTime < event.startTime ? 0.5 : 1,
                  border: isActive ? '2px solid' : 'none',
                  borderColor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(4px)'
                  }
                }}
              >
                <ListItemIcon>
                  {event.type === 'dwell' ? (
                    <DwellIcon color={isActive ? 'primary' : 'action'} />
                  ) : event.type === 'observation' ? (
                    <ObservationIcon color={isActive ? 'primary' : 'action'} />
                  ) : (
                    <TravelIcon color={isActive ? 'primary' : 'action'} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Typography component="span" variant="subtitle1" fontWeight={isActive ? 'bold' : 'normal'}>
                        {event.type === 'dwell' ? 'Dwelling' : event.type === 'observation' ? 'Observed' : 'Traveling'}
                      </Typography>
                      <Chip
                        label={event.deviceType}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {event.type === 'observation' && (
                        <Chip
                          label={`${event.pointCount || 1} pt${(event.pointCount || 1) > 1 ? 's' : ''}`}
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </span>
                  }
                  secondary={
                    <span style={{ display: 'block', marginTop: '4px' }}>
                      {/* Time range */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <TimeIcon sx={{ fontSize: 14 }} />
                        <Typography component="span" variant="caption">
                          {event.startTime.toLocaleTimeString()} - {event.endTime.toLocaleTimeString()}
                          {' '}({formatDuration(duration)})
                        </Typography>
                      </span>

                      {/* Location info for dwell and observation */}
                      {(event.type === 'dwell' || event.type === 'observation') && event.location && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                          <LocationIcon sx={{ fontSize: 14 }} />
                          <Typography component="span" variant="caption">
                            {event.address}
                          </Typography>
                        </span>
                      )}

                      {/* Metadata for observations (GeoNeutron data) */}
                      {event.type === 'observation' && event.metadata && (
                        <span style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          {event.metadata.country && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {event.metadata.country}
                            </Typography>
                          )}
                          {event.metadata.carrier && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {event.metadata.carrier}
                            </Typography>
                          )}
                          {event.metadata.timezone && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {event.metadata.timezone}
                            </Typography>
                          )}
                        </span>
                      )}

                      {/* Travel info */}
                      {event.type === 'travel' && event.distance !== undefined && (
                        <span style={{ display: 'flex', gap: '16px' }}>
                          <Typography component="span" variant="caption">
                            Distance: {formatDistance(event.distance)}
                          </Typography>
                          <Typography component="span" variant="caption">
                            {event.pointCount} GPS points
                          </Typography>
                        </span>
                      )}
                    </span>
                  }
                />
              </ListItem>
              {index < timelineEvents.length - 1 && <Divider sx={{ my: 0.5 }} />}
            </React.Fragment>
          );
        })}
        </List>
      )}

      {/* AI Insights Tab */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, overflow: 'auto', mt: 2 }}>
          {!aiSummary && !isLoadingAI && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generate an AI-powered summary of the timeline activity
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AiIcon />}
                onClick={generateAISummary}
              >
                Generate AI Summary
              </Button>
            </Box>
          )}
          
          {isLoadingAI && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Analyzing {timelineEvents.length} timeline events...
              </Typography>
            </Box>
          )}
          
          {aiSummary && !isLoadingAI && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  AI-Generated Timeline Analysis
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={generateAISummary}
                  disabled={isLoadingAI}
                >
                  Regenerate
                </Button>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {aiSummary}
                </ReactMarkdown>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Export status indicator */}
      {isExporting && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 9999,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
            boxShadow: 4
          }}
        >
          <CircularProgress size={20} color="inherit" />
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Exporting to PowerPoint
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {exportStatus}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Theme Selection Dialog */}
      <Dialog 
        open={themeDialogOpen} 
        onClose={() => setThemeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" component="span">
              Select PowerPoint Theme
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose a visual style for your exported presentation
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setThemeDialogOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {(Object.entries(THEME_DEFINITIONS) as [PowerPointTheme, typeof THEME_DEFINITIONS[PowerPointTheme]][]).map(([themeKey, themeDef]) => (
              <Grid item xs={12} sm={6} md={4} key={themeKey}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTheme === themeKey ? '2px solid' : '1px solid',
                    borderColor: selectedTheme === themeKey ? 'primary.main' : 'divider',
                    bgcolor: selectedTheme === themeKey ? 'action.selected' : 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => setSelectedTheme(themeKey)}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Theme preview colors */}
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 0.5,
                        bgcolor: `#${themeDef.colors.titleBg}`
                      }} />
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 0.5,
                        bgcolor: `#${themeDef.colors.primary}`
                      }} />
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 0.5,
                        bgcolor: `#${themeDef.colors.accent}`
                      }} />
                      <Box sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 0.5,
                        bgcolor: `#${themeDef.colors.contentBg}`,
                        border: '1px solid',
                        borderColor: 'divider'
                      }} />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {themeDef.name}
                      </Typography>
                      {selectedTheme === themeKey && (
                        <CheckIcon color="primary" sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {themeDef.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setThemeDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => exportToPowerPoint(selectedTheme)} 
            variant="contained" 
            color="primary"
            startIcon={<PowerPointIcon />}
          >
            Export with {THEME_DEFINITIONS[selectedTheme].name}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoryMode;

